import { useEffect, useState } from "react";
import { Modal, Button, Typography, Space, Divider, message, DatePicker, Input, Select, Popconfirm, Spin } from "antd";
import {
    EditOutlined,
    CheckCircleOutlined,
    StopOutlined,
    ReloadOutlined,
    CloseOutlined,
    PlusOutlined,
    DeleteOutlined,
    FileTextOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import ApiClient from "../api";

const api_client = new ApiClient();
const { Text } = Typography;

const fmt = (iso) =>
    new Date(iso).toLocaleDateString("en-GB", {
        day: "numeric",
        month: "short",
        year: "numeric",
    });

const ROLE_OPTIONS = ["CLINIC", "INSURER", "AUDIDOX"];

const ROLE_COLORS = {
    CLINIC:  { bg: "rgba(121,167,165,0.15)", color: "#79A7A5", border: "#216361" },
    INSURER: { bg: "rgba(212,160,23,0.12)",  color: "#D4A017", border: "#D4A017" },
    AUDIDOX: { bg: "rgba(33,99,97,0.25)",    color: "#B4D3D1", border: "#79A7A5" },
};

const INPUT_STYLE = {
    background: "#0D0F14",
    border: "1px solid #216361",
    borderRadius: 6,
    color: "#F0F8F7",
    fontSize: 13,
    fontFamily: "'Space Grotesk', sans-serif",
    width: "100%",
};

const styles = {
    wrapper: {
        display: "flex",
        height: "100vh",
        background: "#0D0F14",
        fontFamily: "'Space Grotesk', Inter, 'Helvetica Neue', Arial, sans-serif",
    },
    left: {
        width: "50%",
        borderRight: "1px solid #216361",
        overflowY: "auto",
        padding: "16px",
        display: "flex",
        flexDirection: "column",
        gap: 6,
    },
    panelHeader: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        marginBottom: 6,
    },
    panelTitle: {
        color: "#F0F8F7",
        fontSize: 20,
        fontWeight: 600,
        margin: 0,
        fontFamily: "'Space Grotesk', sans-serif",
    },
    card: {
        background: "#161C1C",
        border: "1px solid #216361",
        borderRadius: 10,
        padding: "10px 14px",
        cursor: "pointer",
        transition: "border-color 150ms ease-in-out, background 150ms ease-in-out",
    },
    cardHover: {
        background: "#1E2828",
        borderColor: "#79A7A5",
    },
    cardActive: {
        background: "#1E2828",
        borderColor: "#79A7A5",
        boxShadow: "0 0 0 1px #79A7A5",
    },
    cardTop: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        marginBottom: 5,
    },
    company: {
        color: "#F0F8F7",
        fontSize: 13,
        fontWeight: 600,
        margin: 0,
        fontFamily: "'Space Grotesk', sans-serif",
    },
    licenseVal: {
        fontFamily: "monospace",
        fontSize: 11,
        color: "#A0B5B4",
        background: "#0D0F14",
        padding: "2px 6px",
        borderRadius: 4,
        marginBottom: 6,
        display: "block",
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
    },
    metaGrid: {
        display: "grid",
        gridTemplateColumns: "1fr 1fr",
        gap: "2px 16px",
        marginBottom: 6,
    },
    metaLabel: {
        fontSize: 10,
        color: "#6B8585",
        textTransform: "uppercase",
        letterSpacing: "0.05em",
        display: "block",
    },
    metaValue: { fontSize: 11, color: "#A0B5B4" },
    statusRow: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        marginTop: 2,
    },
    // RIGHT PANEL
    right: {
        width: "50%",
        background: "#0D0F14",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
    },
    rightEmpty: {
        flex: 1,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 12,
        userSelect: "none",
    },
    rightPanel: {
        flex: 1,
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
    },
    rightHeader: {
        padding: "16px 20px 12px",
        borderBottom: "1px solid #216361",
        flexShrink: 0,
    },
    rightHeaderTop: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        marginBottom: 4,
    },
    rightHeaderCompany: {
        color: "#F0F8F7",
        fontSize: 16,
        fontWeight: 600,
        margin: 0,
        fontFamily: "'Space Grotesk', sans-serif",
    },
    rightHeaderSub: {
        color: "#6B8585",
        fontSize: 12,
        fontFamily: "'Space Grotesk', sans-serif",
    },
    policyList: {
        flex: 1,
        overflowY: "auto",
        padding: "12px 20px",
        display: "flex",
        flexDirection: "column",
        gap: 6,
    },
    policyItem: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        background: "#161C1C",
        border: "1px solid #216361",
        borderRadius: 8,
        padding: "9px 12px",
        transition: "border-color 150ms ease-in-out",
    },
    policyNumber: {
        fontFamily: "monospace",
        fontSize: 13,
        color: "#F0F8F7",
        fontWeight: 500,
    },
    addPolicyRow: {
        padding: "12px 20px",
        borderTop: "1px solid #216361",
        display: "flex",
        gap: 8,
        flexShrink: 0,
    },
    // MODAL
    formRow: {
        display: "flex",
        flexDirection: "column",
        gap: 4,
        marginBottom: 12,
    },
    formLabel: {
        fontSize: 11,
        color: "#6B8585",
        textTransform: "uppercase",
        letterSpacing: "0.05em",
        fontFamily: "'Space Grotesk', sans-serif",
    },
};

const modalStyles = {
    content: { background: "#161C1C", border: "1px solid #216361", borderRadius: 12, padding: 0 },
    header:  { background: "#161C1C", borderBottom: "1px solid #216361", borderRadius: "12px 12px 0 0", padding: "16px 20px" },
    body:    { padding: "20px", background: "#161C1C", borderRadius: "0 0 12px 12px" },
    mask:    { background: "rgba(13,15,20,0.75)" },
};

const FieldRow = ({ label, value, mono }) => (
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "9px 0", borderBottom: "1px solid #216361" }}>
        <Text style={{ color: "#A0B5B4", fontSize: 13 }}>{label}</Text>
        <Text style={{ color: "#F0F8F7", fontSize: mono ? 11 : 13, fontWeight: 500, fontFamily: mono ? "monospace" : "'Space Grotesk', sans-serif", maxWidth: 200, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {value}
        </Text>
    </div>
);

const EMPTY_FORM = { license: "", company: "", email: "", roletype: "CLINIC", issued: null, expires: null };

const AdminPanel = () => {
    const [licenses, setLicenses]               = useState([]);
    const [selected, setSelected]               = useState(null);
    const [hoveredId, setHoveredId]             = useState(null);
    const [activeLic, setActiveLic]             = useState(null);   // card clicked — shows right panel
    const [policies, setPolicies]               = useState([]);
    const [policiesLoading, setPoliciesLoading] = useState(false);
    const [newPolicy, setNewPolicy]             = useState("");
    const [addPolicyLoading, setAddPolicyLoading] = useState(false);
    const [deletingPolicy, setDeletingPolicy]   = useState(null);   // policynumber being deleted
    const [addOpen, setAddOpen]                 = useState(false);
    const [addForm, setAddForm]                 = useState(EMPTY_FORM);
    const [addLoading, setAddLoading]           = useState(false);
    const [actionLoading, setActionLoading]     = useState(false);
    const [messageApi, contextHolder]           = message.useMessage();

    // ─── Load licenses ──────────────────────────────────────
    useEffect(() => {
        const load = async () => {
            const accessData = localStorage.getItem("user_access");
            if (!accessData) return;
            try {
                JSON.parse(accessData);
                const data = await api_client.adminlistlicenses();
                setLicenses(data);
            } catch (e) {
                console.error("Failed to load licenses", e);
            }
        };
        load();
    }, []);

    // ─── Click card → load policies ─────────────────────────
    const handleLicenseClick = async (lic) => {
        setActiveLic(lic);
        setPolicies([]);
        setNewPolicy("");
        setPoliciesLoading(true);
        try {
            const data = await api_client.getpoliciesbylicense(lic.license);
            setPolicies(data);
        } catch (e) {
            messageApi.error({ content: "Failed to load policies", style: { fontFamily: "'Space Grotesk', sans-serif" } });
        } finally {
            setPoliciesLoading(false);
        }
    };

    // ─── Add policy ─────────────────────────────────────────
    const handleAddPolicy = async () => {
        if (!newPolicy.trim()) return;
        setAddPolicyLoading(true);
        try {
            await api_client.createpolicynumber(newPolicy.trim(), activeLic.license);
            const data = await api_client.getpoliciesbylicense(activeLic.license);
            setPolicies(data);
            setNewPolicy("");
            messageApi.success({ content: "Policy added", style: { fontFamily: "'Space Grotesk', sans-serif" } });
        } catch (e) {
            messageApi.error({ content: "Failed to add policy", style: { fontFamily: "'Space Grotesk', sans-serif" } });
        } finally {
            setAddPolicyLoading(false);
        }
    };

    // ─── Delete policy ──────────────────────────────────────
    const handleDeletePolicy = async (policynumber) => {
        setDeletingPolicy(policynumber);
        try {
            await api_client.deletepolicynumber(policynumber, activeLic.license);
            setPolicies((prev) => prev.filter((p) => p.policynumber !== policynumber));
            messageApi.success({ content: "Policy removed", style: { fontFamily: "'Space Grotesk', sans-serif" } });
        } catch (e) {
            messageApi.error({ content: "Failed to delete policy", style: { fontFamily: "'Space Grotesk', sans-serif" } });
        } finally {
            setDeletingPolicy(null);
        }
    };

    // ─── Toggle active ──────────────────────────────────────
    const handleToggle = async () => {
        const updated = { ...selected, active: !selected.active };
        setActionLoading(true);
        try {
            await api_client.adminupdatelicense(selected.license, selected.issued, selected.expires, selected.email, selected.roletype, updated.active);
            setLicenses((prev) => prev.map((l) => (l.id === selected.id ? updated : l)));
            if (activeLic?.id === selected.id) setActiveLic(updated);
            setSelected(updated);
            messageApi.open({ type: updated.active ? "success" : "warning", content: `License ${updated.active ? "enabled" : "disabled"} successfully`, style: { fontFamily: "'Space Grotesk', sans-serif" } });
        } catch (e) {
            messageApi.error({ content: "Failed to update license", style: { fontFamily: "'Space Grotesk', sans-serif" } });
        } finally {
            setActionLoading(false);
        }
    };

    // ─── Renew +1 year ──────────────────────────────────────
    const handleRenew = async () => {
        const newExp = new Date(selected.expires);
        newExp.setFullYear(newExp.getFullYear() + 1);
        const updated = { ...selected, expires: newExp.toISOString() };
        setActionLoading(true);
        try {
            await api_client.adminupdatelicense(selected.license, selected.issued, updated.expires, selected.email, selected.roletype, selected.active);
            setLicenses((prev) => prev.map((l) => (l.id === selected.id ? updated : l)));
            if (activeLic?.id === selected.id) setActiveLic(updated);
            setSelected(updated);
            messageApi.success({ content: `Renewed — expires ${fmt(updated.expires)}`, style: { fontFamily: "'Space Grotesk', sans-serif" } });
        } catch (e) {
            messageApi.error({ content: "Failed to renew license", style: { fontFamily: "'Space Grotesk', sans-serif" } });
        } finally {
            setActionLoading(false);
        }
    };

    // ─── Remove license ─────────────────────────────────────
    const handleRemove = async () => {
        setActionLoading(true);
        try {
            await api_client.adminremovelicense(selected.license);
            setLicenses((prev) => prev.filter((l) => l.id !== selected.id));
            if (activeLic?.id === selected.id) { setActiveLic(null); setPolicies([]); }
            setSelected(null);
            messageApi.success({ content: "License removed", style: { fontFamily: "'Space Grotesk', sans-serif" } });
        } catch (e) {
            messageApi.error({ content: "Failed to remove license", style: { fontFamily: "'Space Grotesk', sans-serif" } });
        } finally {
            setActionLoading(false);
        }
    };

    // ─── Add license ────────────────────────────────────────
    const handleAdd = async () => {
        const { license, company, email, roletype, issued, expires } = addForm;
        if (!license || !company || !email || !roletype || !issued || !expires) {
            messageApi.warning({ content: "All fields are required", style: { fontFamily: "'Space Grotesk', sans-serif" } });
            return;
        }
        setAddLoading(true);
        try {
            await api_client.adminaddlicense(license, company, issued.toISOString(), expires.toISOString(), email, roletype);
            const data = await api_client.adminlistlicenses();
            setLicenses(data);
            setAddOpen(false);
            setAddForm(EMPTY_FORM);
            messageApi.success({ content: "License added", style: { fontFamily: "'Space Grotesk', sans-serif" } });
        } catch (e) {
            messageApi.error({ content: "Failed to add license", style: { fontFamily: "'Space Grotesk', sans-serif" } });
        } finally {
            setAddLoading(false);
        }
    };

    // ─── Role tag ────────────────────────────────────────────
    const roleTag = (role) => {
        const c = ROLE_COLORS[role] || ROLE_COLORS.AUDIDOX;
        return (
            <span style={{ fontSize: 10, fontWeight: 500, padding: "1px 7px", borderRadius: 5, border: `1px solid ${c.border}`, background: c.bg, color: c.color, letterSpacing: "0.04em", textTransform: "uppercase", fontFamily: "'Space Grotesk', sans-serif", whiteSpace: "nowrap" }}>
                {role}
            </span>
        );
    };

    const darkPickerStyle = { ...INPUT_STYLE };
    const darkSelectStyle = { width: "100%", fontFamily: "'Space Grotesk', sans-serif" };

    return (
        <>
            {contextHolder}
            <link href="https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@300;400;500;600;700&display=swap" rel="stylesheet" />

            <div style={styles.wrapper}>

                {/* ── LEFT — license cards ── */}
                <div style={styles.left}>
                    <div style={styles.panelHeader}>
                        <p style={styles.panelTitle}>Licenses</p>
                        <Button
                            icon={<PlusOutlined />}
                            size="small"
                            onClick={() => {
                                const generatedUuid = crypto.randomUUID().toUpperCase();
                                setAddOpen(true);
                                setAddForm({ ...EMPTY_FORM, license: generatedUuid });
                            }}
                            style={{ fontSize: 12, height: 28, background: "#216361", border: "none", color: "#F0F8F7", borderRadius: 6, fontFamily: "'Space Grotesk', sans-serif", padding: "0 10px" }}
                        >
                            Add
                        </Button>
                    </div>

                    {licenses.map((lic) => {
                        const isActive = activeLic?.id === lic.id;
                        const isHovered = hoveredId === lic.id;
                        return (
                            <div
                                key={lic.id}
                                style={{
                                    ...styles.card,
                                    ...(isActive ? styles.cardActive : isHovered ? styles.cardHover : {}),
                                }}
                                onMouseEnter={() => setHoveredId(lic.id)}
                                onMouseLeave={() => setHoveredId(null)}
                                onClick={() => handleLicenseClick(lic)}
                            >
                                <div style={styles.cardTop}>
                                    <p style={styles.company}>{lic.company}</p>
                                    {roleTag(lic.roletype)}
                                </div>

                                <span style={styles.licenseVal}>{lic.license}</span>

                                <div style={styles.metaGrid}>
                                    <div>
                                        <span style={styles.metaLabel}>Issued</span>
                                        <span style={styles.metaValue}>{fmt(lic.issued)}</span>
                                    </div>
                                    <div>
                                        <span style={styles.metaLabel}>Expires</span>
                                        <span style={styles.metaValue}>{fmt(lic.expires)}</span>
                                    </div>
                                </div>

                                <div style={styles.statusRow}>
                                    <Space size={6}>
                                        <span style={{ width: 6, height: 6, borderRadius: "50%", background: lic.active ? "#79A7A5" : "#E57373", display: "inline-block" }} />
                                        <Text style={{ fontSize: 11, color: lic.active ? "#79A7A5" : "#E57373" }}>
                                            {lic.active ? "Active" : "Disabled"}
                                        </Text>
                                    </Space>

                                    <Button
                                        size="small"
                                        icon={<EditOutlined />}
                                        onClick={(e) => { e.stopPropagation(); setSelected(lic); }}
                                        style={{ fontSize: 11, height: 24, background: "transparent", border: "1px solid #216361", color: "#79A7A5", borderRadius: 6, fontFamily: "'Space Grotesk', sans-serif", transition: "all 150ms ease-in-out", padding: "0 8px" }}
                                        onMouseEnter={(e) => { e.currentTarget.style.background = "#1E2828"; e.currentTarget.style.borderColor = "#79A7A5"; e.currentTarget.style.color = "#B4D3D1"; }}
                                        onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; e.currentTarget.style.borderColor = "#216361"; e.currentTarget.style.color = "#79A7A5"; }}
                                    >
                                        Edit
                                    </Button>
                                </div>
                            </div>
                        );
                    })}
                </div>

                {/* ── RIGHT — policies panel ── */}
                <div style={styles.right}>
                    {!activeLic ? (
                        <div style={styles.rightEmpty}>
                            <svg width="40" height="40" viewBox="0 0 40 40" fill="none">
                                <rect x="4"  y="28" width="6" height="8"  rx="1" fill="#216361" opacity="0.4" />
                                <rect x="13" y="20" width="6" height="16" rx="1" fill="#216361" opacity="0.6" />
                                <rect x="22" y="12" width="6" height="24" rx="1" fill="#216361" opacity="0.8" />
                                <rect x="31" y="4"  width="6" height="32" rx="1" fill="#216361" />
                            </svg>
                            <span style={{ color: "#6B8585", fontSize: 13 }}>Click a license to view policies</span>
                        </div>
                    ) : (
                        <div style={styles.rightPanel}>

                            {/* Header */}
                            <div style={styles.rightHeader}>
                                <div style={styles.rightHeaderTop}>
                                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                                        <p style={styles.rightHeaderCompany}>{activeLic.company}</p>
                                        {roleTag(activeLic.roletype)}
                                    </div>
                                    <Space size={6}>
                                        <span style={{ width: 6, height: 6, borderRadius: "50%", background: activeLic.active ? "#79A7A5" : "#E57373", display: "inline-block" }} />
                                        <Text style={{ fontSize: 12, color: activeLic.active ? "#79A7A5" : "#E57373" }}>
                                            {activeLic.active ? "Active" : "Disabled"}
                                        </Text>
                                    </Space>
                                </div>
                                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                                    <span style={styles.rightHeaderSub}>{activeLic.email}</span>
                                    <span style={{ fontSize: 11, color: "#6B8585", fontFamily: "monospace" }}>
                                        {policies.length} {policies.length === 1 ? "policy" : "policies"}
                                    </span>
                                </div>
                            </div>

                            {/* Policy list */}
                            <div style={styles.policyList}>
                                {policiesLoading ? (
                                    <div style={{ display: "flex", justifyContent: "center", paddingTop: 32 }}>
                                        <Spin size="small" style={{ color: "#79A7A5" }} />
                                    </div>
                                ) : policies.length === 0 ? (
                                    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 8, paddingTop: 40, color: "#6B8585" }}>
                                        <FileTextOutlined style={{ fontSize: 24, color: "#216361", opacity: 0.5 }} />
                                        <span style={{ fontSize: 13 }}>No policies on this license</span>
                                    </div>
                                ) : (
                                    policies.map((p) => (
                                        <div
                                            key={p.policynumber}
                                            style={styles.policyItem}
                                            onMouseEnter={(e) => e.currentTarget.style.borderColor = "#79A7A5"}
                                            onMouseLeave={(e) => e.currentTarget.style.borderColor = "#216361"}
                                        >
                                            <Space size={8}>
                                                <FileTextOutlined style={{ color: "#79A7A5", fontSize: 13 }} />
                                                <span style={styles.policyNumber}>{p.policynumber}</span>
                                            </Space>

                                            <Popconfirm
                                                title="Remove this policy?"
                                                onConfirm={() => handleDeletePolicy(p.policynumber)}
                                                okText="Remove"
                                                cancelText="Cancel"
                                                okButtonProps={{ danger: true, style: { fontFamily: "'Space Grotesk', sans-serif" } }}
                                                cancelButtonProps={{ style: { fontFamily: "'Space Grotesk', sans-serif", background: "transparent", border: "1px solid #216361", color: "#79A7A5" } }}
                                            >
                                                <Button
                                                    size="small"
                                                    icon={<DeleteOutlined />}
                                                    loading={deletingPolicy === p.policynumber}
                                                    style={{ fontSize: 11, height: 24, background: "transparent", border: "1px solid #C0392B", color: "#E57373", borderRadius: 6, padding: "0 8px", transition: "all 150ms ease-in-out" }}
                                                    onMouseEnter={(e) => { e.currentTarget.style.background = "rgba(192,57,43,0.12)"; }}
                                                    onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}
                                                />
                                            </Popconfirm>
                                        </div>
                                    ))
                                )}
                            </div>

                            {/* Add policy input */}
                            <div style={styles.addPolicyRow}>
                                <Input
                                    placeholder="Enter policy number…"
                                    value={newPolicy}
                                    onChange={(e) => setNewPolicy(e.target.value)}
                                    onPressEnter={handleAddPolicy}
                                    style={{ ...INPUT_STYLE, flex: 1 }}
                                />
                                <Button
                                    icon={<PlusOutlined />}
                                    loading={addPolicyLoading}
                                    onClick={handleAddPolicy}
                                    style={{ height: 32, background: "#216361", border: "none", color: "#F0F8F7", borderRadius: 6, fontFamily: "'Space Grotesk', sans-serif", fontWeight: 500, fontSize: 13, padding: "0 14px", transition: "all 150ms ease-in-out" }}
                                    onMouseEnter={(e) => { e.currentTarget.style.background = "#1E2828"; e.currentTarget.style.color = "#B4D3D1"; e.currentTarget.style.border = "1px solid #79A7A5"; }}
                                    onMouseLeave={(e) => { e.currentTarget.style.background = "#216361"; e.currentTarget.style.color = "#F0F8F7"; e.currentTarget.style.border = "none"; }}
                                >
                                    Add
                                </Button>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* ══ EDIT MODAL ══════════════════════════════════════ */}
            <Modal
                open={!!selected}
                onCancel={() => setSelected(null)}
                footer={null}
                closeIcon={<CloseOutlined style={{ color: "#79A7A5", fontSize: 14 }} />}
                width={400}
                styles={{ content: modalStyles.content, header: modalStyles.header, body: modalStyles.body, mask: modalStyles.mask }}
                title={
                    selected && (
                        <div>
                            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 2 }}>
                                <Text style={{ color: "#F0F8F7", fontSize: 16, fontWeight: 600, fontFamily: "'Space Grotesk', sans-serif" }}>
                                    {selected.company}
                                </Text>
                                {roleTag(selected.roletype)}
                            </div>
                            <Text style={{ color: "#6B8585", fontSize: 12 }}>{selected.email}</Text>
                        </div>
                    )
                }
            >
                {selected && (
                    <>
                        <FieldRow label="License key" value={selected.license} mono />
                        <FieldRow label="Corporate"   value={selected.corporate} />
                        <FieldRow label="Issued"      value={fmt(selected.issued)} />
                        <FieldRow label="Expires"     value={fmt(selected.expires)} />
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "9px 0" }}>
                            <Text style={{ color: "#A0B5B4", fontSize: 13 }}>Status</Text>
                            <Space size={6}>
                                <span style={{ width: 7, height: 7, borderRadius: "50%", background: selected.active ? "#79A7A5" : "#E57373", display: "inline-block" }} />
                                <Text style={{ fontSize: 13, fontWeight: 500, color: selected.active ? "#79A7A5" : "#E57373" }}>
                                    {selected.active ? "Active" : "Disabled"}
                                </Text>
                            </Space>
                        </div>

                        <Divider style={{ borderColor: "#216361", margin: "14px 0" }} />

                        <Space style={{ width: "100%", marginBottom: 8 }} size={8}>
                            <Button
                                icon={selected.active ? <StopOutlined /> : <CheckCircleOutlined />}
                                loading={actionLoading}
                                onClick={handleToggle}
                                style={{ flex: 1, fontFamily: "'Space Grotesk', sans-serif", fontWeight: 500, fontSize: 13, height: 36, background: "transparent", border: `1px solid ${selected.active ? "#C0392B" : "#216361"}`, color: selected.active ? "#E57373" : "#79A7A5", borderRadius: 8, transition: "all 150ms ease-in-out" }}
                            >
                                {selected.active ? "Disable" : "Enable"}
                            </Button>
                            <Button
                                icon={<ReloadOutlined />}
                                loading={actionLoading}
                                onClick={handleRenew}
                                style={{ flex: 1, fontFamily: "'Space Grotesk', sans-serif", fontWeight: 500, fontSize: 13, height: 36, background: "#216361", border: "1px solid #216361", color: "#F0F8F7", borderRadius: 8, transition: "all 150ms ease-in-out" }}
                                onMouseEnter={(e) => { e.currentTarget.style.background = "#1E2828"; e.currentTarget.style.borderColor = "#79A7A5"; e.currentTarget.style.color = "#B4D3D1"; }}
                                onMouseLeave={(e) => { e.currentTarget.style.background = "#216361"; e.currentTarget.style.borderColor = "#216361"; e.currentTarget.style.color = "#F0F8F7"; }}
                            >
                                Renew 1 year
                            </Button>
                        </Space>

                        <Popconfirm
                            title="Remove this license?"
                            description="This action cannot be undone."
                            onConfirm={handleRemove}
                            okText="Remove"
                            cancelText="Cancel"
                            okButtonProps={{ danger: true, style: { fontFamily: "'Space Grotesk', sans-serif" } }}
                            cancelButtonProps={{ style: { fontFamily: "'Space Grotesk', sans-serif", background: "transparent", border: "1px solid #216361", color: "#79A7A5" } }}
                        >
                            <Button
                                icon={<DeleteOutlined />}
                                loading={actionLoading}
                                style={{ width: "100%", fontFamily: "'Space Grotesk', sans-serif", fontWeight: 500, fontSize: 13, height: 36, background: "transparent", border: "1px solid #C0392B", color: "#E57373", borderRadius: 8, transition: "all 150ms ease-in-out" }}
                                onMouseEnter={(e) => { e.currentTarget.style.background = "rgba(192,57,43,0.1)"; }}
                                onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}
                            >
                                Remove license
                            </Button>
                        </Popconfirm>
                    </>
                )}
            </Modal>

            {/* ══ ADD LICENSE MODAL ════════════════════════════════ */}
            <Modal
                open={addOpen}
                onCancel={() => { setAddOpen(false); setAddForm(EMPTY_FORM); }}
                footer={null}
                closeIcon={<CloseOutlined style={{ color: "#79A7A5", fontSize: 14 }} />}
                width={420}
                styles={{ content: modalStyles.content, header: modalStyles.header, body: modalStyles.body, mask: modalStyles.mask }}
                title={<Text style={{ color: "#F0F8F7", fontSize: 16, fontWeight: 600, fontFamily: "'Space Grotesk', sans-serif" }}>Add license</Text>}
            >
                <div style={styles.formRow}>
                    <span style={styles.formLabel}>License key</span>
                    <Input placeholder="e.g. XXXXX-XXXXX-XXXXX-XXXXX" value={addForm.license} onChange={(e) => setAddForm((f) => ({ ...f, license: e.target.value }))} style={INPUT_STYLE} />
                </div>
                <div style={styles.formRow}>
                    <span style={styles.formLabel}>Company</span>
                    <Input placeholder="Company name" value={addForm.company} onChange={(e) => setAddForm((f) => ({ ...f, company: e.target.value }))} style={INPUT_STYLE} />
                </div>
                <div style={styles.formRow}>
                    <span style={styles.formLabel}>Email</span>
                    <Input placeholder="contact@company.com" value={addForm.email} onChange={(e) => setAddForm((f) => ({ ...f, email: e.target.value }))} style={INPUT_STYLE} />
                </div>
                <div style={styles.formRow}>
                    <span style={styles.formLabel}>Role type</span>
                    <Select value={addForm.roletype} onChange={(val) => setAddForm((f) => ({ ...f, roletype: val }))} style={darkSelectStyle} options={ROLE_OPTIONS.map((r) => ({ label: r, value: r }))} popupMatchSelectWidth />
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 12 }}>
                    <div style={styles.formRow}>
                        <span style={styles.formLabel}>Issued</span>
                        <DatePicker value={addForm.issued ? dayjs(addForm.issued) : null} onChange={(d) => setAddForm((f) => ({ ...f, issued: d ? d.toDate() : null }))} style={darkPickerStyle} format="DD MMM YYYY" />
                    </div>
                    <div style={styles.formRow}>
                        <span style={styles.formLabel}>Expires</span>
                        <DatePicker value={addForm.expires ? dayjs(addForm.expires) : null} onChange={(d) => setAddForm((f) => ({ ...f, expires: d ? d.toDate() : null }))} style={darkPickerStyle} format="DD MMM YYYY" />
                    </div>
                </div>
                <Divider style={{ borderColor: "#216361", margin: "4px 0 14px" }} />
                <Space style={{ width: "100%" }} size={8}>
                    <Button onClick={() => { setAddOpen(false); setAddForm(EMPTY_FORM); }} style={{ flex: 1, fontFamily: "'Space Grotesk', sans-serif", fontSize: 13, height: 36, background: "transparent", border: "1px solid #216361", color: "#79A7A5", borderRadius: 8 }}>Cancel</Button>
                    <Button loading={addLoading} onClick={handleAdd} style={{ flex: 1, fontFamily: "'Space Grotesk', sans-serif", fontWeight: 500, fontSize: 13, height: 36, background: "#216361", border: "1px solid #216361", color: "#F0F8F7", borderRadius: 8 }}
                            onMouseEnter={(e) => { e.currentTarget.style.background = "#1E2828"; e.currentTarget.style.borderColor = "#79A7A5"; e.currentTarget.style.color = "#B4D3D1"; }}
                            onMouseLeave={(e) => { e.currentTarget.style.background = "#216361"; e.currentTarget.style.borderColor = "#216361"; e.currentTarget.style.color = "#F0F8F7"; }}
                    >Add license</Button>
                </Space>
            </Modal>
        </>
    );
};

export default AdminPanel;